import { MultiAreaChart } from "@shared/components/Charts/MultiAreaChart";
import type { MultiAreaSeries } from "@shared/components/Charts/MultiAreaChart";
import type { GraphSlot } from "@shared/utils/graphColors";
import { JACHIN_REPORTING_ENABLED } from "@solomon-apps/config";
import { formatCurrency, formatCurrencyCompact } from "@solomon-apps/shared";
import { Text } from "@solomon-labs/design/components/ui";
import { graphColorValue } from "@solomon-labs/design/tokens/graph";
import type { GraphColor } from "@solomon-labs/design/tokens/graph";
import React from "react";

import { reserveHistoryRows } from "@/hooks/stats/reserveHistory";
import { useReportingReserveHistory } from "@/hooks/stats/useReportingReserveHistory";
import { useReserveHistory } from "@/hooks/stats/useReserveHistory";
import type { ReserveAssetRow } from "@/hooks/stats/useReservesData";

const useSelectedReserveHistory = JACHIN_REPORTING_ENABLED
    ? useReportingReserveHistory
    : useReserveHistory;

interface ReserveHistoryChartProps {
    /** The class's assets, newest figures, in display order. */
    assets: ReserveAssetRow[];
    /** Page-unique, since the chart mints `<linearGradient>` ids from it. */
    idPrefix: string;
    /** The page's one symbol-to-colour map, so a token reads the same in the
     *  bar, its row swatch and this chart. */
    colorBySymbol: Map<string, GraphColor>;
}

export function ReserveHistoryChart({
    assets,
    idPrefix,
    colorBySymbol
}: ReserveHistoryChartProps): React.ReactElement | null {
    const history = useSelectedReserveHistory();
    const data = React.useMemo(
        () =>
            reserveHistoryRows(
                history.data ?? [],
                assets.map((asset) => asset.symbol)
            ),
        [history.data, assets]
    );

    const series: MultiAreaSeries[] = React.useMemo(
        () =>
            assets.map((asset) => {
                const color = colorBySymbol.get(asset.symbol);
                return {
                    key: asset.symbol,
                    name: asset.symbol,
                    color: color ? graphColorValue(color) : "transparent",
                    // The legend is off here, and `slot` only backs its swatch
                    // class, so it never renders. Derived anyway rather than
                    // faked: GraphColor is the GraphSlot name prefixed.
                    slot: (color ?? "graph-primary").replace(
                        "graph-",
                        ""
                    ) as GraphSlot
                };
            }),
        [assets, colorBySymbol]
    );

    if (assets.length === 0) return null;

    const lastObserved = [...data]
        .reverse()
        .find((row) => assets.some((asset) => row[asset.symbol] !== undefined));
    if (!lastObserved) {
        return (
            <Text variant="small" className="text-foreground-muted">
                {history.isPending
                    ? "Loading reserve history…"
                    : history.isError
                      ? "Reserve history could not be loaded."
                      : "No reserve history available."}
            </Text>
        );
    }

    return (
        <div className="flex flex-col pt-2">
            {history.isError && (
                <Text variant="fine-print" className="text-foreground-muted">
                    Could not refresh. Showing saved history.
                </Text>
            )}
            <MultiAreaChart
                inspection="inline"
                data={data}
                series={series}
                gradientIdPrefix={idPrefix}
                valueFormatter={(value) => formatCurrency(value)}
                yTickFormatter={(value) => formatCurrencyCompact(value)}
                height={200}
                showLegend={false}
            />
        </div>
    );
}
